import { GridDataRow, simpleHeader } from "@homebound/beam";
import { ChangeEventType, LotType, ProjectItem } from "src/generated/graphqlTypes";
import { ProjectItemStore, SpecsAndSelectionsRow } from "src/routes/projects/models/ProjectItemStore";
import { DeepPartial, fail } from "src/utils";
import { ProjectContextProps } from "../context/ProjectContext";

export function createSpecsAndSelectionRows(
  piStore: ProjectItemStore,
  showPlanSource: boolean,
): GridDataRow<SpecsAndSelectionsRow>[] {
  return [
    { kind: "totals" as const, id: "totals", data: piStore },
    simpleHeader,
    ...piStore.groupedBy.map((group) => ({
      kind: "group" as const,
      id: group.id,
      data: group,
      children: group.children.map((pi) => ({
        kind: "projectItem" as const,
        id: pi.id,
        data: pi,
        children: showPlanSource
          ? [pi.fragment.itemTemplateItem, ...pi.fragment.modifyingTemplateItems]
              .compact()
              .map((li, i) => ({ kind: "lineItem" as const, id: `${pi.id}-${li.id}-${i}`, data: li }))
          : [],
      })),
      // sortValue defined for each ObservableProjectItemGroup in ProjectItemStore
      sortValue: group.sortValue,
    })),
  ];
}

export function isUnallocatedCost(projectItem: DeepPartial<ProjectItem>): boolean {
  return !projectItem.task?.id && projectItem.totalCostInCents !== 0;
}

export function getIsLocationRequired(projectType: string): boolean {
  return !["Community", "Subdivision", "Lot"].includes(projectType);
}

/** The change event type a project defaults to, i.e. BOOL projects are chargeable and everything else is not. */
export function defaultChangeEventTypeByLotType(
  project: Pick<ProjectContextProps, "lotType" | "shouldAccrueAsComplete">,
  disabledOptions?: (string | { value: string; reason: string })[],
) {
  const { lotType = fail("lotType is required"), shouldAccrueAsComplete } = project;
  const changeEventTypeByLotType: Record<LotType, ChangeEventType> = {
    [LotType.Bool]: ChangeEventType.External,
    [LotType.Hbl]: ChangeEventType.Internal,
    [LotType.Boyl]: ChangeEventType.Internal,
    [LotType.Btr]: ChangeEventType.Internal,
    [LotType.Fee]: ChangeEventType.Internal,
  };
  // Certain projects have an agreement where every cost after completion should be HTI's cost
  // therefore we shouldn't create chargeable change events post-completion
  const defaultValue = shouldAccrueAsComplete ? ChangeEventType.Internal : changeEventTypeByLotType[lotType];
  // Return undefined if the default value is disabled
  const disabledValues = disabledOptions?.map((o) => (typeof o === "object" ? o.value : o)).compact() ?? [];
  return disabledValues.includes(defaultValue) ? undefined : defaultValue;
}
